import type React from 'react';
import { useContext, useEffect } from 'react';
import { Auth, useAuth } from '@zg-rentals/auth-comet';
import { useDispatch } from 'react-redux';
import { Redirect, Route, useLocation } from 'react-router';
import { setUser } from '../../slices/user';
import { RMWContext } from '../../providers/RMWContext';
import StringConstants from '../../constants/StringConstants';

import { authRedirects } from '@zg-rentals/auth-comet';

// Note: useful as client-side redirects to login route should trigger SSR for rental-growth-web
const IsomorphicRedirect = ({ to }: { to: string }) => {
  if (typeof window !== 'undefined') {
    window.location.href = to;
    return null;
  } else {
    return <Redirect to={to} />;
  }
};

const AuthCheckWrapper = ({ children }: { children: React.ReactNode }) => {
  const { isInMobileWebView, brand } = useContext(RMWContext);

  const dispatch = useDispatch();
  const { pathname, search } = useLocation();

  // Note: synchronizes user state with Redux on the client, ensuring Redux user and auth-comet user
  // are equivalent
  const { user } = useAuth();
  useEffect(() => {
    if (user) dispatch(setUser(user));
  }, [user]);

  return (
    <Auth.Boundary
      onUnauthorized={() => (
        <Route
          render={({ staticContext }) => {
            if (staticContext) {
              staticContext.statusCode = 302;
            }

            // Note: safe to use Redirect on client here as this route does live in ZRMW
            if (__DEV__) {
              return <Redirect to={'/rental-manager/featureFlags'} />;
            }

            // If in mobile view, and a native login page is defined, route to that
            const manifest = authRedirects[brand as keyof typeof authRedirects] ?? authRedirects.zillow;
            if (isInMobileWebView && 'nativeAppSignInPage' in manifest) {
              const redirectTo = manifest.nativeAppSignInPage?.(pathname, search);
              if (redirectTo) {
                return <IsomorphicRedirect to={redirectTo} />;
              }
            }

            // Otherwise just route to sign in page
            return (
              <IsomorphicRedirect
                to={manifest.signInPage(
                  pathname,
                  search,
                  brand === StringConstants.UNFORMATTED_BRAND_NAMES.zillow ? '/rental-manager/login/' : undefined,
                )}
              />
            );
          }}
        />
      )}
    >
      {children}
    </Auth.Boundary>
  );
};

export default AuthCheckWrapper;
